import type { DocsDocumentSummary } from '@/bridge/types';
import { App, Button, Empty, Input, Modal, Skeleton } from 'antd';
import {
  BookOpen,
  ChevronDown,
  ChevronRight,
  Download,
  FilePlus2,
  Folder,
  FolderPlus,
  GripVertical,
  Import,
  Info,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type DragEvent as ReactDragEvent,
} from 'react';
import biLogo from '../../../../src/app/assets/images/logo.svg';
import { docsApi, type DocsDirectorySummary } from './api';

interface DocumentCenterProps {
  onOpenDocument(documentId: string): void;
}

type DialogState =
  | { kind: 'create-document'; title: string }
  | {
      kind: 'rename-document';
      document: DocsDocumentSummary;
      title: string;
    }
  | { kind: 'create-directory'; parentPath: string[]; name: string }
  | { kind: 'rename-directory'; path: string[]; name: string };

interface DocumentTreeBranch {
  name: string;
  sortOrder: number;
  children: Map<string, DocumentTreeBranch>;
  documents: DocsDocumentSummary[];
}

interface DocumentTreeNode {
  key: string;
  name: string;
  path: string[];
  sortOrder: number;
  documentCount: number;
  children: DocumentTreeNode[];
  documents: DocsDocumentSummary[];
}

type DropPlacement = 'before' | 'after' | 'inside';

function compareDirectoryNames(left: string, right: string) {
  if (left === '未分类') return 1;
  if (right === '未分类') return -1;
  return left.localeCompare(right, 'zh-CN');
}

function compareDirectoryBranches(
  left: DocumentTreeBranch,
  right: DocumentTreeBranch,
) {
  return (
    right.sortOrder - left.sortOrder ||
    compareDirectoryNames(left.name, right.name)
  );
}

function sortDocuments(items: DocsDocumentSummary[]) {
  return [...items].sort(
    (left, right) =>
      (right.sortOrder || 0) - (left.sortOrder || 0) ||
      new Date(right.updatedAt).getTime() -
        new Date(left.updatedAt).getTime() ||
      left.title.localeCompare(right.title, 'zh-CN'),
  );
}

function isSameDocumentPath(left: string[] = [], right: string[] = []) {
  return (
    left.length === right.length &&
    left.every((segment, index) => segment === right[index])
  );
}

function isDirectoryPathPrefix(path: string[], prefix: string[]) {
  return (
    path.length >= prefix.length &&
    prefix.every((segment, index) => path[index] === segment)
  );
}

function moveDocumentLocally(
  items: DocsDocumentSummary[],
  documentId: string,
  targetPath: string[],
  beforeDocumentId?: string,
) {
  const movedDocument = items.find(document => document.id === documentId);
  if (!movedDocument || documentId === beforeDocumentId) {
    return items;
  }

  const normalizedTargetPath = targetPath
    .map(segment => segment.trim())
    .filter(Boolean);
  const targetDocuments = sortDocuments(
    items.filter(
      document =>
        document.id !== documentId &&
        isSameDocumentPath(document.breadcrumb, normalizedTargetPath),
    ),
  );
  const insertIndex = beforeDocumentId
    ? targetDocuments.findIndex(document => document.id === beforeDocumentId)
    : targetDocuments.length;
  if (insertIndex < 0) {
    return items;
  }

  const nextTargetDocuments = [...targetDocuments];
  nextTargetDocuments.splice(insertIndex, 0, movedDocument);
  const currentTargetIds = sortDocuments(
    items.filter(document =>
      isSameDocumentPath(document.breadcrumb, normalizedTargetPath),
    ),
  ).map(document => document.id);
  const nextTargetIds = nextTargetDocuments.map(document => document.id);
  if (
    isSameDocumentPath(movedDocument.breadcrumb, normalizedTargetPath) &&
    currentTargetIds.every(
      (currentDocumentId, index) => currentDocumentId === nextTargetIds[index],
    )
  ) {
    return items;
  }

  const nextSortOrders = new Map(
    nextTargetDocuments.map((document, index) => [
      document.id,
      (nextTargetDocuments.length - index) * 100,
    ]),
  );
  const movedAt = new Date().toISOString();
  return items.map(document => {
    const sortOrder = nextSortOrders.get(document.id);
    if (document.id === documentId) {
      return {
        ...document,
        breadcrumb: normalizedTargetPath,
        sortOrder,
        updatedAt: movedAt,
      };
    }
    return sortOrder !== undefined && document.sortOrder !== sortOrder
      ? { ...document, sortOrder }
      : document;
  });
}

interface LocalDirectoryMove {
  destinationPath: string[];
  directories: DocsDirectorySummary[];
  documents: DocsDocumentSummary[];
}

function replaceDirectoryPathPrefix(
  path: string[],
  sourcePath: string[],
  destinationPath: string[],
) {
  return [...destinationPath, ...path.slice(sourcePath.length)];
}

function compareDirectorySummaries(
  left: DocsDirectorySummary,
  right: DocsDirectorySummary,
) {
  return (
    (right.sortOrder || 0) - (left.sortOrder || 0) ||
    compareDirectoryNames(
      left.path[left.path.length - 1] || '',
      right.path[right.path.length - 1] || '',
    )
  );
}

function moveDirectoryLocally(
  documents: DocsDocumentSummary[],
  directories: DocsDirectorySummary[],
  sourcePath: string[],
  targetParentPath: string[],
  beforePath?: string[],
): LocalDirectoryMove | undefined {
  const movedDirectory = directories.find(directory =>
    isSameDocumentPath(directory.path, sourcePath),
  );
  if (!movedDirectory) return undefined;

  const destinationPath = [
    ...targetParentPath,
    sourcePath[sourcePath.length - 1],
  ];
  const targetSiblings = directories
    .filter(
      directory =>
        directory.path.length === targetParentPath.length + 1 &&
        isDirectoryPathPrefix(directory.path, targetParentPath) &&
        !isSameDocumentPath(directory.path, sourcePath),
    )
    .sort(compareDirectorySummaries);
  const insertIndex = beforePath
    ? targetSiblings.findIndex(directory =>
        isSameDocumentPath(directory.path, beforePath),
      )
    : targetSiblings.length;
  if (insertIndex < 0) return undefined;

  const orderedTargetSiblings = [...targetSiblings];
  orderedTargetSiblings.splice(insertIndex, 0, {
    path: destinationPath,
    sortOrder: movedDirectory.sortOrder,
  });
  const targetSortOrders = new Map(
    orderedTargetSiblings.map((directory, index) => [
      JSON.stringify(directory.path),
      (orderedTargetSiblings.length - index) * 100,
    ]),
  );
  const pathChanged = !isSameDocumentPath(sourcePath, destinationPath);
  let directoriesChanged = false;
  const nextDirectories = directories.map(directory => {
    const path =
      pathChanged && isDirectoryPathPrefix(directory.path, sourcePath)
        ? replaceDirectoryPathPrefix(
            directory.path,
            sourcePath,
            destinationPath,
          )
        : directory.path;
    const sortOrder =
      targetSortOrders.get(JSON.stringify(path)) ?? directory.sortOrder;
    if (
      path !== directory.path ||
      (sortOrder || 0) !== (directory.sortOrder || 0)
    ) {
      directoriesChanged = true;
      return { ...directory, path, sortOrder };
    }
    return directory;
  });

  let documentsChanged = false;
  const movedAt = new Date().toISOString();
  const nextDocuments = pathChanged
    ? documents.map(document => {
        const breadcrumb = document.breadcrumb || [];
        if (!isDirectoryPathPrefix(breadcrumb, sourcePath)) return document;
        documentsChanged = true;
        return {
          ...document,
          breadcrumb: replaceDirectoryPathPrefix(
            breadcrumb,
            sourcePath,
            destinationPath,
          ),
          updatedAt: movedAt,
        };
      })
    : documents;

  if (!directoriesChanged && !documentsChanged) return undefined;
  return {
    destinationPath,
    directories: nextDirectories,
    documents: nextDocuments,
  };
}

function remapCollapsedDirectoryKeys(
  keys: Set<string>,
  sourcePath: string[],
  destinationPath: string[],
) {
  if (isSameDocumentPath(sourcePath, destinationPath)) return keys;
  let changed = false;
  const next = new Set<string>();
  keys.forEach(key => {
    try {
      const path = JSON.parse(key) as unknown;
      if (
        Array.isArray(path) &&
        path.every(segment => typeof segment === 'string') &&
        isDirectoryPathPrefix(path, sourcePath)
      ) {
        next.add(
          JSON.stringify(
            replaceDirectoryPathPrefix(path, sourcePath, destinationPath),
          ),
        );
        changed = true;
        return;
      }
    } catch {
      // Preserve unrelated keys that are not serialized directory paths.
    }
    next.add(key);
  });
  return changed ? next : keys;
}

function countDocuments(branch: DocumentTreeBranch): number {
  return (
    branch.documents.length +
    [...branch.children.values()].reduce(
      (count, child) => count + countDocuments(child),
      0,
    )
  );
}

function buildDocumentTree(
  documents: DocsDocumentSummary[],
  directories: DocsDirectorySummary[],
) {
  const root: DocumentTreeBranch = {
    name: '',
    sortOrder: 0,
    children: new Map(),
    documents: [],
  };

  const ensurePath = (path: string[], sortOrder?: number) => {
    let branch = root;
    path
      .map(name => name.trim())
      .filter(Boolean)
      .forEach(name => {
        let child = branch.children.get(name);
        if (!child) {
          child = { name, sortOrder: 0, children: new Map(), documents: [] };
          branch.children.set(name, child);
        }
        branch = child;
      });
    if (sortOrder !== undefined) {
      branch.sortOrder = sortOrder;
    }
    return branch;
  };

  directories.forEach(directory =>
    ensurePath(directory.path, directory.sortOrder || 0),
  );
  documents.forEach(document => {
    const path = (document.breadcrumb || [])
      .map(name => name.trim())
      .filter(Boolean);
    ensurePath(path.length ? path : ['未分类']).documents.push(document);
  });

  const toNode = (
    branch: DocumentTreeBranch,
    parentPath: string[],
  ): DocumentTreeNode => {
    const path = [...parentPath, branch.name];
    return {
      key: JSON.stringify(path),
      name: branch.name,
      path,
      sortOrder: branch.sortOrder,
      documentCount: countDocuments(branch),
      children: [...branch.children.values()]
        .sort(compareDirectoryBranches)
        .map(child => toNode(child, path)),
      documents: sortDocuments(branch.documents),
    };
  };

  return [...root.children.values()]
    .sort(compareDirectoryBranches)
    .map(branch => toNode(branch, []));
}

function findDirectoryChildren(
  tree: DocumentTreeNode[],
  parentPath: string[],
) {
  let children = tree;
  for (const segment of parentPath) {
    const parent = children.find(node => node.name === segment);
    if (!parent) return [];
    children = parent.children;
  }
  return children;
}

function isUncategorizedDirectory(node: DocumentTreeNode) {
  return node.path.length === 1 && node.name === '未分类';
}

function displayDate(value?: string) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
}

function isPublished(document: DocsDocumentSummary) {
  return (
    document.status === 'published' ||
    document.revision.id === document.publishedRevisionId
  );
}

export function DocumentCenter({ onOpenDocument }: DocumentCenterProps) {
  const { message, modal } = App.useApp();
  const importInputRef = useRef<HTMLInputElement>(null);
  const [documents, setDocuments] = useState<DocsDocumentSummary[]>([]);
  const [directories, setDirectories] = useState<DocsDirectorySummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState('');
  const [dialog, setDialog] = useState<DialogState>();
  const [collapsedKeys, setCollapsedKeys] = useState<Set<string>>(new Set());
  const [draggingDocumentId, setDraggingDocumentId] = useState('');
  const [draggingDirectoryPath, setDraggingDirectoryPath] = useState<string[]>(
    [],
  );
  const [dropTargetKey, setDropTargetKey] = useState('');
  const [dropPlacement, setDropPlacement] = useState<DropPlacement>('inside');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [nextDocuments, nextDirectories] = await Promise.all([
        docsApi.listDocuments(),
        docsApi.listDirectories(),
      ]);
      setDocuments(nextDocuments);
      setDirectories(nextDirectories);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '文档加载失败');
    } finally {
      setLoading(false);
    }
  }, [message]);

  useEffect(() => {
    void load();
  }, [load]);

  const tree = useMemo(
    () => buildDocumentTree(documents, directories),
    [directories, documents],
  );

  const closeDialog = () => {
    if (!saving) setDialog(undefined);
  };

  const confirmDialog = async () => {
    if (!dialog) return;
    setSaving(true);
    try {
      if (dialog.kind === 'create-document') {
        const title = dialog.title.trim();
        if (!title) throw new Error('请输入文档标题');
        const created = await docsApi.createDocument({ title, breadcrumb: [] });
        setDialog(undefined);
        onOpenDocument(created.id);
        return;
      }
      if (dialog.kind === 'rename-document') {
        const title = dialog.title.trim();
        if (!title) throw new Error('请输入文档标题');
        await docsApi.updateDocument(dialog.document.id, {
          title,
          breadcrumb: dialog.document.breadcrumb || [],
        });
      } else if (dialog.kind === 'create-directory') {
        const name = dialog.name.trim();
        if (!name) throw new Error('请输入分类名称');
        await docsApi.createDirectory([...dialog.parentPath, name]);
      } else if (dialog.kind === 'rename-directory') {
        const name = dialog.name.trim();
        if (!name) throw new Error('请输入分类名称');
        await docsApi.renameDirectory(dialog.path, name);
      }
      setDialog(undefined);
      await load();
      message.success('操作成功');
    } catch (error) {
      message.error(error instanceof Error ? error.message : '操作失败');
    } finally {
      setSaving(false);
    }
  };

  const archiveDocument = (document: DocsDocumentSummary) => {
    modal.confirm({
      title: `确定删除文档“${document.title}”吗？`,
      content: '删除后文档将被归档，不再出现在公开文档列表中。',
      okText: '删除',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        setBusyId(document.id);
        try {
          await docsApi.archiveDocument(document.id);
          await load();
          message.success('文档已删除');
        } catch (error) {
          message.error(error instanceof Error ? error.message : '删除失败');
          throw error;
        } finally {
          setBusyId('');
        }
      },
    });
  };

  const deleteDirectory = (node: DocumentTreeNode) => {
    const parentName = node.path[node.path.length - 2];
    modal.confirm({
      title:
        node.path.length === 1
          ? `删除目录“${node.name}”后，其中 ${node.documentCount} 篇文档将转为未分类，是否继续？`
          : `删除目录“${node.name}”后，其中 ${node.documentCount} 篇文档将上移到“${parentName}”，是否继续？`,
      okText: '删除目录',
      okButtonProps: { danger: true },
      cancelText: '取消',
      onOk: async () => {
        try {
          await docsApi.deleteDirectory(node.path);
          await load();
          message.success('目录已删除');
        } catch (error) {
          message.error(
            error instanceof Error ? error.message : '目录删除失败',
          );
          throw error;
        }
      },
    });
  };

  const exportDocument = async (document: DocsDocumentSummary) => {
    setBusyId(document.id);
    try {
      const { blob, fileName, externalAssetCount } =
        await docsApi.downloadDocument(document.id);
      const url = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');
      anchor.href = url;
      anchor.download =
        fileName ||
        `${
          document.title.replace(/[\\/:*?"<>|]/g, '-') || 'document'
        }.posbi-doc.zip`;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      if (externalAssetCount > 0) {
        message.success(
          `文档包已导出，${externalAssetCount} 个图片/视频保留 CDN 地址，导入后需源 CDN 资源可访问`,
          8,
        );
      } else {
        message.success('文档包已导出');
      }
    } catch (error) {
      message.error(error instanceof Error ? error.message : '导出失败');
    } finally {
      setBusyId('');
    }
  };

  const importDocument = async (file: File) => {
    setBusyId('import');
    try {
      const imported = await docsApi.importDocument(file);
      message.success('文档包已导入');
      onOpenDocument(imported.id);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '导入失败');
      throw error;
    } finally {
      setBusyId('');
      if (importInputRef.current) importInputRef.current.value = '';
    }
  };

  const selectDocumentPackage = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0];
    event.currentTarget.value = '';
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.zip')) {
      message.error('请选择 .zip 文档包');
      return;
    }
    if (file.size > 200 * 1024 * 1024) {
      message.error('文档包不能超过 200MB');
      return;
    }
    modal.confirm({
      title: '导入文档包',
      content: `将“${file.name}”导入为草稿。导入后请检查内容，再手动发布。`,
      okText: '导入为草稿',
      cancelText: '取消',
      onOk: () => importDocument(file),
    });
  };

  const moveDocument = async (
    documentId: string,
    targetPath: string[],
    beforeDocumentId?: string,
  ) => {
    if (!documentId || documentId === beforeDocumentId || busyId) return;
    const previousDocuments = documents;
    const nextDocuments = moveDocumentLocally(
      previousDocuments,
      documentId,
      targetPath,
      beforeDocumentId,
    );
    if (nextDocuments === previousDocuments) {
      setDraggingDocumentId('');
      setDropTargetKey('');
      return;
    }
    setBusyId(documentId);
    setDocuments(nextDocuments);
    try {
      const moved = await docsApi.moveDocument(
        documentId,
        targetPath,
        beforeDocumentId,
      );
      if (!moved) {
        throw new Error('文档位置未更新');
      }
    } catch (error) {
      setDocuments(current =>
        current === nextDocuments ? previousDocuments : current,
      );
      message.error(error instanceof Error ? error.message : '移动失败');
    } finally {
      setBusyId('');
      setDraggingDocumentId('');
      setDropTargetKey('');
      setDropPlacement('inside');
    }
  };

  const moveDirectory = async (
    path: string[],
    targetPath: string[],
    beforePath?: string[],
  ) => {
    if (!path.length || busyId) return;
    const previousDocuments = documents;
    const previousDirectories = directories;
    const localMove = moveDirectoryLocally(
      previousDocuments,
      previousDirectories,
      path,
      targetPath,
      beforePath,
    );
    if (!localMove) {
      setDraggingDirectoryPath([]);
      setDropTargetKey('');
      setDropPlacement('inside');
      return;
    }
    const { destinationPath } = localMove;
    const directoryBusyId = `directory:${JSON.stringify(destinationPath)}`;
    setBusyId(directoryBusyId);
    setDocuments(localMove.documents);
    setDirectories(localMove.directories);
    setCollapsedKeys(current =>
      remapCollapsedDirectoryKeys(current, path, destinationPath),
    );
    try {
      const moved = await docsApi.moveDirectory(path, targetPath, beforePath);
      if (!moved) {
        throw new Error('目录位置未更新');
      }
    } catch (error) {
      setDocuments(current =>
        current === localMove.documents ? previousDocuments : current,
      );
      setDirectories(current =>
        current === localMove.directories ? previousDirectories : current,
      );
      setCollapsedKeys(current =>
        remapCollapsedDirectoryKeys(current, destinationPath, path),
      );
      message.error(error instanceof Error ? error.message : '目录移动失败');
    } finally {
      setBusyId('');
      setDraggingDirectoryPath([]);
      setDropTargetKey('');
      setDropPlacement('inside');
    }
  };

  const resolveDocumentDropPosition = (
    event: ReactDragEvent<HTMLDivElement>,
    documentId: string,
    targetPath: string[],
    targetDocumentId: string,
  ) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    if (event.clientY < bounds.top + bounds.height / 2) {
      return targetDocumentId;
    }
    const siblings = sortDocuments(
      documents.filter(
        document =>
          document.id !== documentId &&
          isSameDocumentPath(document.breadcrumb, targetPath),
      ),
    );
    const targetIndex = siblings.findIndex(
      document => document.id === targetDocumentId,
    );
    return targetIndex < 0 ? targetDocumentId : siblings[targetIndex + 1]?.id;
  };

  const documentDragProps = (
    targetKey: string,
    targetPath: string[],
    targetDocumentId?: string,
  ) => ({
    onDragOver: (event: ReactDragEvent<HTMLDivElement>) => {
      if (!draggingDocumentId || busyId) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setDropTargetKey(targetKey);
      if (!targetDocumentId) {
        setDropPlacement('inside');
        return;
      }
      const bounds = event.currentTarget.getBoundingClientRect();
      setDropPlacement(
        event.clientY < bounds.top + bounds.height / 2 ? 'before' : 'after',
      );
    },
    onDragLeave: (event: ReactDragEvent<HTMLDivElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node)) return;
      setDropTargetKey(current => (current === targetKey ? '' : current));
      setDropPlacement('inside');
    },
    onDrop: (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const documentId =
        event.dataTransfer.getData('text/plain') || draggingDocumentId;
      const beforeDocumentId = targetDocumentId
        ? resolveDocumentDropPosition(
            event,
            documentId,
            targetPath,
            targetDocumentId,
          )
        : undefined;
      void moveDocument(documentId, targetPath, beforeDocumentId);
    },
  });

  const resolveDirectoryDropPosition = (
    event: ReactDragEvent<HTMLDivElement>,
    sourcePath: string[],
    targetNode: DocumentTreeNode,
  ) => {
    if (isUncategorizedDirectory(targetNode)) return undefined;
    const bounds = event.currentTarget.getBoundingClientRect();
    const offsetRatio = (event.clientY - bounds.top) / bounds.height;
    if (offsetRatio >= 0.25 && offsetRatio <= 0.75) {
      return isDirectoryPathPrefix(targetNode.path, sourcePath)
        ? undefined
        : {
            targetPath: targetNode.path,
            beforePath: undefined,
            placement: 'inside' as DropPlacement,
          };
    }

    if (isSameDocumentPath(sourcePath, targetNode.path)) {
      return undefined;
    }
    const targetParentPath = targetNode.path.slice(0, -1);
    if (isDirectoryPathPrefix(targetParentPath, sourcePath)) {
      return undefined;
    }
    if (offsetRatio < 0.25) {
      return {
        targetPath: targetParentPath,
        beforePath: targetNode.path,
        placement: 'before' as DropPlacement,
      };
    }

    const siblings = findDirectoryChildren(tree, targetParentPath).filter(
      sibling => !isSameDocumentPath(sibling.path, sourcePath),
    );
    const targetIndex = siblings.findIndex(sibling =>
      isSameDocumentPath(sibling.path, targetNode.path),
    );
    if (targetIndex < 0) return undefined;
    return {
      targetPath: targetParentPath,
      beforePath: siblings[targetIndex + 1]?.path,
      placement: 'after' as DropPlacement,
    };
  };

  const categoryDragProps = (node: DocumentTreeNode) => ({
    onDragOver: (event: ReactDragEvent<HTMLDivElement>) => {
      if (busyId) return;
      if (draggingDocumentId) {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
        setDropTargetKey(node.key);
        setDropPlacement('inside');
        return;
      }
      if (!draggingDirectoryPath.length) return;
      const position = resolveDirectoryDropPosition(
        event,
        draggingDirectoryPath,
        node,
      );
      if (!position) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = 'move';
      setDropTargetKey(node.key);
      setDropPlacement(position.placement);
    },
    onDragLeave: (event: ReactDragEvent<HTMLDivElement>) => {
      if (event.currentTarget.contains(event.relatedTarget as Node)) return;
      setDropTargetKey(current => (current === node.key ? '' : current));
      setDropPlacement('inside');
    },
    onDrop: (event: ReactDragEvent<HTMLDivElement>) => {
      event.preventDefault();
      if (draggingDocumentId) {
        const documentId =
          event.dataTransfer.getData('text/plain') || draggingDocumentId;
        void moveDocument(
          documentId,
          isUncategorizedDirectory(node) ? [] : node.path,
        );
        return;
      }
      if (!draggingDirectoryPath.length) return;
      const position = resolveDirectoryDropPosition(
        event,
        draggingDirectoryPath,
        node,
      );
      if (!position) return;
      void moveDirectory(
        draggingDirectoryPath,
        position.targetPath,
        position.beforePath,
      );
    },
  });

  const toggleNode = (key: string) => {
    setCollapsedKeys(current => {
      const next = new Set(current);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const renderDocument = (document: DocsDocumentSummary, depth: number) => {
    const key = `document:${document.id}`;
    const path = document.breadcrumb || [];
    return (
      <div
        className={`docs-center-tree-row docs-center-tree-document ${
          dropTargetKey === key ? `is-drop-target is-drop-${dropPlacement}` : ''
        } ${draggingDocumentId === document.id ? 'is-dragging' : ''} ${
          busyId === document.id ? 'is-busy' : ''
        }`}
        draggable={!busyId}
        key={key}
        {...documentDragProps(key, path, document.id)}
        onDragStart={event => {
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData('text/plain', document.id);
          setDraggingDocumentId(document.id);
        }}
        onDragEnd={() => {
          setDraggingDocumentId('');
          setDropTargetKey('');
          setDropPlacement('inside');
        }}
      >
        {depth > 0 ? (
          <span
            className="docs-center-tree-connector"
            style={{ left: 16 + (depth - 1) * 24 }}
          />
        ) : null}
        <div
          className="docs-center-tree-main"
          style={{ paddingLeft: 12 + depth * 24 }}
        >
          <span className="docs-center-drag-handle" title="拖动排序或移动">
            <GripVertical size={15} />
          </span>
          <BookOpen className="docs-center-document-icon" size={15} />
          <div className="docs-center-document-name">
            <strong
              title="双击修改文档标题"
              onDoubleClick={() =>
                setDialog({
                  kind: 'rename-document',
                  document,
                  title: document.title,
                })
              }
            >
              {document.title}
            </strong>
            <div className="docs-center-document-meta">
              <span className={isPublished(document) ? 'is-published' : ''}>
                {isPublished(document) ? '已发布' : '草稿'}
              </span>
              <span>v{document.revision.revisionNo || 1}</span>
              <i>·</i>
              <span>{displayDate(document.updatedAt)}</span>
            </div>
          </div>
        </div>
        <div className="docs-center-tree-actions">
          <Button
            icon={<BookOpen size={14} />}
            size="small"
            type="link"
            onClick={() => onOpenDocument(document.id)}
          >
            打开
          </Button>
          <Button
            icon={<Download size={14} />}
            loading={busyId === document.id}
            size="small"
            type="link"
            onClick={() => void exportDocument(document)}
          >
            导出
          </Button>
          <Button
            danger
            icon={<Trash2 size={14} />}
            loading={busyId === document.id}
            size="small"
            type="link"
            onClick={() => archiveDocument(document)}
          >
            删除
          </Button>
        </div>
      </div>
    );
  };

  const renderNode = (node: DocumentTreeNode, depth = 0): React.ReactNode[] => {
    const collapsed = collapsedKeys.has(node.key);
    const uncategorized = isUncategorizedDirectory(node);
    const hasChildren = Boolean(node.children.length || node.documents.length);
    const rows: React.ReactNode[] = [
      <div
        className={`docs-center-tree-row docs-center-tree-category ${
          dropTargetKey === node.key
            ? `is-drop-target is-drop-${dropPlacement}`
            : ''
        } ${
          isSameDocumentPath(draggingDirectoryPath, node.path)
            ? 'is-dragging'
            : ''
        } ${
          busyId === `directory:${JSON.stringify(node.path)}` ? 'is-busy' : ''
        }`}
        draggable={!uncategorized && !busyId}
        key={`category:${node.key}`}
        {...categoryDragProps(node)}
        onDragStart={event => {
          if (uncategorized) {
            event.preventDefault();
            return;
          }
          event.dataTransfer.effectAllowed = 'move';
          event.dataTransfer.setData(
            'application/x-posbi-directory',
            JSON.stringify(node.path),
          );
          event.dataTransfer.setData(
            'text/plain',
            `directory:${JSON.stringify(node.path)}`,
          );
          setDraggingDirectoryPath(node.path);
        }}
        onDragEnd={() => {
          setDraggingDirectoryPath([]);
          setDropTargetKey('');
          setDropPlacement('inside');
        }}
      >
        {depth > 0 ? (
          <span
            className="docs-center-tree-connector"
            style={{ left: 16 + (depth - 1) * 24 }}
          />
        ) : null}
        <div
          className="docs-center-tree-main"
          style={{ paddingLeft: 12 + depth * 24 }}
        >
          {!uncategorized ? (
            <span className="docs-center-drag-handle" title="拖动排序或移动">
              <GripVertical size={15} />
            </span>
          ) : null}
          {hasChildren ? (
            <Button
              aria-label={collapsed ? '展开目录' : '收起目录'}
              className="docs-center-expand"
              icon={
                collapsed ? (
                  <ChevronRight size={12} />
                ) : (
                  <ChevronDown size={12} />
                )
              }
              size="small"
              type="text"
              onClick={() => toggleNode(node.key)}
            />
          ) : (
            <span className="docs-center-expand-spacer" />
          )}
          <Folder className="docs-center-folder-icon" size={15} />
          <strong
            className="docs-center-category-name"
            title={uncategorized ? undefined : '双击修改分类名称'}
            onDoubleClick={
              uncategorized
                ? undefined
                : () =>
                    setDialog({
                      kind: 'rename-directory',
                      path: node.path,
                      name: node.name,
                    })
            }
          >
            {node.name}
          </strong>
          <span className="docs-center-category-count">
            {node.documentCount} 篇
          </span>
        </div>
        {!uncategorized ? (
          <div className="docs-center-tree-actions is-subtle">
            <Button
              icon={<Plus size={14} />}
              size="small"
              type="link"
              onClick={() =>
                setDialog({
                  kind: 'create-directory',
                  parentPath: node.path,
                  name: '',
                })
              }
            >
              新增子级
            </Button>
            <Button
              danger
              icon={<Trash2 size={14} />}
              size="small"
              type="link"
              onClick={() => deleteDirectory(node)}
            >
              删除
            </Button>
          </div>
        ) : null}
      </div>,
    ];

    if (!collapsed) {
      node.children.forEach(child =>
        rows.push(...renderNode(child, depth + 1)),
      );
      node.documents.forEach(document =>
        rows.push(renderDocument(document, depth + 1)),
      );
    }
    return rows;
  };

  return (
    <main className="docs-center-page">
      <header className="docs-center-header">
        <div>
          <h1>
            <img src={biLogo} alt="PosBI" />
            PosBI 文档中心
          </h1>
          <p>查看供所有用户使用的产品知识文档。</p>
        </div>
        <div className="docs-center-actions">
          <Button
            icon={<Import size={16} />}
            loading={busyId === 'import'}
            onClick={() => importInputRef.current?.click()}
          >
            导入文档包
          </Button>
          <Button
            icon={<FilePlus2 size={16} />}
            type="primary"
            onClick={() => setDialog({ kind: 'create-document', title: '' })}
          >
            新建文档
          </Button>
          <input
            ref={importInputRef}
            hidden
            accept=".zip,application/zip"
            type="file"
            onChange={selectDocumentPackage}
          />
        </div>
      </header>

      <section className="docs-center-panel">
        <div className="docs-center-toolbar">
          <span>
            <Info size={14} />
            拖动文档或目录可调整顺序和位置
          </span>
          <Button
            icon={<FolderPlus size={15} />}
            size="small"
            onClick={() =>
              setDialog({
                kind: 'create-directory',
                parentPath: [],
                name: '',
              })
            }
          >
            新增根分类
          </Button>
        </div>
        {loading ? (
          <div className="docs-center-loading">
            <Skeleton active paragraph={{ rows: 12 }} />
          </div>
        ) : tree.length ? (
          <div className="docs-center-tree">
            {tree.flatMap(node => renderNode(node))}
          </div>
        ) : (
          <div className="docs-center-empty">
            <Empty description="暂无文档">
              <Button
                icon={<FilePlus2 size={16} />}
                type="primary"
                onClick={() =>
                  setDialog({ kind: 'create-document', title: '' })
                }
              >
                新建文档
              </Button>
            </Empty>
          </div>
        )}
      </section>

      <Modal
        destroyOnHidden
        confirmLoading={saving}
        open={Boolean(dialog)}
        title={
          dialog?.kind === 'create-document'
            ? '新建文档'
            : dialog?.kind === 'rename-document'
            ? '重命名文档'
            : dialog?.kind === 'create-directory'
            ? dialog.parentPath.length
              ? '新增子分类'
              : '新增根分类'
            : '重命名分类'
        }
        okText={dialog?.kind === 'create-document' ? '创建并编辑' : '确定'}
        cancelText="取消"
        onCancel={closeDialog}
        onOk={() => void confirmDialog()}
      >
        {dialog?.kind === 'create-directory' && dialog.parentPath.length ? (
          <div className="docs-center-parent-hint">
            <Folder size={16} />
            <span>
              将创建在 <strong>{dialog.parentPath.join(' / ')}</strong> 下
            </span>
          </div>
        ) : null}
        {dialog ? (
          <label className="docs-center-dialog-field">
            {dialog.kind === 'create-document' ||
            dialog.kind === 'rename-document'
              ? '文档标题'
              : '分类名称'}
            <Input
              autoFocus
              maxLength={
                dialog.kind === 'create-document' ||
                dialog.kind === 'rename-document'
                  ? 255
                  : 64
              }
              placeholder={
                dialog.kind === 'create-document'
                  ? '例如：计算字段与表达式'
                  : undefined
              }
              value={
                dialog.kind === 'create-document' ||
                dialog.kind === 'rename-document'
                  ? dialog.title
                  : dialog.name
              }
              onChange={event => {
                if (
                  dialog.kind === 'create-document' ||
                  dialog.kind === 'rename-document'
                ) {
                  setDialog({ ...dialog, title: event.target.value });
                } else {
                  setDialog({ ...dialog, name: event.target.value });
                }
              }}
              onPressEnter={() => void confirmDialog()}
            />
          </label>
        ) : null}
      </Modal>
    </main>
  );
}
